/**
 * Global Token Store (Zustand)
 * Single source of truth for token balance — prevents N simultaneous API calls
 * when multiple components mount and each calls useTokens() independently.
 */

import { create } from 'zustand';
import { API_URL } from '@/lib/config';
import { authFetch } from '@/utils/api-headers';

interface TokenBreakdown {
  free: number;
  subscription: number;
  rollover: number;
  reserve: number;
  bonus: number;
}

interface TokenState {
  balance: number;
  breakdown: TokenBreakdown;
  subscriptionTier: 'free' | 'pro' | 'business' | 'enterprise';
  monthlyAllowance: number;
  monthlyUsed: number;
  resetAt: string | null;
  totalPurchased: number;
  totalUsed: number;
  loading: boolean;
  lastFetchedAt: number | null;
  userId: string | null;

  // Actions
  fetchBalance: (userId: string, force?: boolean) => Promise<void>;
  setBalance: (data: Partial<TokenState>) => void;
  hasEnoughTokens: (required: number) => boolean;
}

const CACHE_TTL_MS = 10_000; // 10s de cache anti-doublon

export const useTokenStore = create<TokenState>((set, get) => ({
  balance: 0,
  breakdown: { free: 0, subscription: 0, rollover: 0, reserve: 0, bonus: 0 },
  subscriptionTier: 'free',
  monthlyAllowance: 100_000,
  monthlyUsed: 0,
  resetAt: null,
  totalPurchased: 0,
  totalUsed: 0,
  loading: true,
  lastFetchedAt: null,
  userId: null,

  fetchBalance: async (userId: string, force = false) => {
    const { lastFetchedAt, loading, userId: currentUserId } = get();

    // Deduplicate: ne pas refetch si on a des données fraîches (< 10s) pour le même user
    const isFresh = lastFetchedAt && Date.now() - lastFetchedAt < CACHE_TTL_MS;
    if (!force && isFresh && currentUserId === userId && !loading) return;

    // Si déjà en cours de fetch (loading=true), ne pas relancer
    if (loading && currentUserId === userId && lastFetchedAt !== null) return;

    set({ loading: true, userId });

    try {
      const response = await authFetch(`${API_URL}/api/tokens/balance/${userId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      set({
        balance: Number(data.total) || 0,
        breakdown: {
          free: Number(data.breakdown?.free) || 0,
          subscription: Number(data.breakdown?.subscription) || 0,
          rollover: Number(data.breakdown?.rollover) || 0,
          reserve: Number(data.breakdown?.reserve) || 0,
          bonus: Number(data.breakdown?.bonus) || 0,
        },
        subscriptionTier: data.subscription_tier || 'free',
        monthlyAllowance: Number(data.monthly_allowance) || 100_000,
        monthlyUsed: Number(data.monthly_used) || 0,
        resetAt: data.reset_at || null,
        totalPurchased: Number(data.tokens_total_purchased) || 0,
        totalUsed: Number(data.tokens_total_used) || 0,
        loading: false,
        lastFetchedAt: Date.now(),
      });
    } catch {
      set({ loading: false, lastFetchedAt: Date.now() });
    }
  },

  setBalance: (data) => set(data as Partial<TokenState>),

  hasEnoughTokens: (required: number) => get().balance >= required,
}));
